import { ModalName } from '@share/enums';
import {
  handleInputMaxLength,
  handleInputNumberOnly,
  showPercentage,
  toggleBootstrapModal,
} from '@util/helper';
import { listing } from '@state/private';
import { market } from '@state/market';
import { useCurrency, useHelper } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DotLoading from '@com/utils/DotLoading';
import ErrorMsg from '@com/utils/ErrorMsg';

interface Params {
  tokenId: number;
  collectionAddress: string;
  royalty: number;
  loadLaunchpadData: () => void;
}

function CreateSaleModal(param: Params): JSX.Element {
  /* Define state / hook */
  const { tokenId, collectionAddress, royalty, loadLaunchpadData } = param;
  const { createSale, isSigned, payment } = market.useContainer();
  const { getCurrencyNameById, getMinimumUnit } = useCurrency();
  const { displayCurrency } = useHelper();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [quantity] = useState(1); // For 721 quantity is always 1
  const { allowedCurrencies, getServiceRate, getHandlingRate, getMethodId } =
    listing.useContainer();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const sellingPrice = Number(watch('sellingPrice'));
  const selectedCurrencyId = Number(watch('sellingCurrencyId'));

  /* Custom Data Object for render or other usages */
  const getRoyaltyFee = (price: number) => {
    return Number(price) * (royalty / 100);
  };

  const getServiceFee = (price: number) => {
    return Number(price) * (getServiceRate(selectedCurrencyId) / 100);
  };

  const getHandlingFee = (price: number) => {
    return Number(price) * (getHandlingRate(selectedCurrencyId) / 100);
  };

  const getTotalPrice = (price: number) => {
    return (
      Number(price) -
      getRoyaltyFee(price) -
      getServiceFee(price) -
      getHandlingFee(price)
    );
  };

  /* Render control function / logic */
  const handleSubmitSell = useDebouncedCallback(
    async () => {
      const isLogin = await payment();
      if (!isLogin) return;
      setIsLoading(true);

      const isTxSuccess = await createSale(
        collectionAddress,
        tokenId,
        selectedCurrencyId,
        getMethodId(selectedCurrencyId),
        sellingPrice,
        quantity
      );

      setIsLoading(false);
      if (!isTxSuccess) return;
      toggleBootstrapModal(ModalName.CREATE_BUY);
      loadLaunchpadData();
    },
    500,
    { leading: true, trailing: false }
  );

  /* Data Change Listeners - useEffect/useMemo/useCallback */
  useEffect(() => {
    // Default value not working, temp solution
    const id = allowedCurrencies?.[0]?.id;
    if (id) setValue('sellingCurrencyId', id);
  }, [allowedCurrencies]);

  /* Styling */

  /* Render */
  return (
    <div
      aria-hidden
      className="modal fade popup"
      id={ModalName.CREATE_BUY}
      role="dialog"
    >
      <div
        className="modal-dialog modal-dialog-centered mt-0 mb-0"
        role="document"
      >
        <div className="modal-content">
          <button
            className="close"
            type="button"
            onClick={() => {
              if (!isLoading) toggleBootstrapModal(ModalName.CREATE_BUY);
            }}
          >
            <span aria-hidden>&times;</span>
          </button>

          <div className="modal-body space-y-20 pd-40">
            <h3>{t('modal.listItemForSale')}</h3>
            <hr />
            {isLoading ? (
              <div>
                <DotLoading />
                <div className="row mt-4">
                  <div
                    className="col-12 text-center mt-5"
                    style={{ fontSize: '2.5em' }}
                  >
                    {isSigned
                      ? t('modal.waitContract')
                      : t('modal.signContract')}
                  </div>
                </div>
              </div>
            ) : (
              <form noValidate onSubmit={handleSubmit(handleSubmitSell)}>
                <p>{t('modal.sellingPrice')}</p>
                <input
                  aria-required
                  aria-label={t('modal.sellingPrice')}
                  className="form-control mt-3"
                  data-test="price-input"
                  inputMode="decimal"
                  maxLength={12}
                  min={0}
                  placeholder={t('modal.egSellingPrice')}
                  step={getMinimumUnit(selectedCurrencyId)}
                  type="number"
                  onKeyDown={(e) =>
                    handleInputMaxLength(e) && handleInputNumberOnly(e)
                  }
                  {...register('sellingPrice', {
                    valueAsNumber: true,
                    required: {
                      value: true,
                      message: t('modal.priceRequired'),
                    },
                    validate: {
                      value: (value) => {
                        const minimumUnit = getMinimumUnit(selectedCurrencyId);
                        const digits = String(value).split('.')?.[1]?.length;
                        const maxDigits =
                          String(minimumUnit).split('.')[1]?.length;

                        return (
                          digits === undefined ||
                          digits <= maxDigits ||
                          t('modal.pricePatternRequired', {
                            min: minimumUnit,
                          })
                        );
                      },
                    },
                  })}
                />
                <ErrorMsg errors={errors} name="sellingPrice" />

                <div className="d-flex justify-content-between mb-5 mt-5">
                  <p className="mt-1">{t('modal.chooseReceiveCurrency')}</p>
                  <div className="col-5 pr-0">
                    <select
                      className="p-2 w-100 text-center bg-transparent rounded-pill"
                      data-test="sell-currency-select"
                      {...register('sellingCurrencyId', {
                        valueAsNumber: true,
                        required: {
                          value: true,
                          message: t('modal.chooseCurrecny'),
                        },
                      })}
                    >
                      {allowedCurrencies?.map((item) => (
                        <option key={item.id} value={item.id.toString()}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    <ErrorMsg errors={errors} name="sellingCurrencyId" />
                  </div>
                </div>

                <div className="d-flex justify-content-between mb-5">
                  <p>
                    {t('modal.royalty', {
                      percentage: showPercentage(royalty),
                    })}
                  </p>
                  <div className="text-right">
                    <div className="price color-popup">
                      {displayCurrency(
                        getRoyaltyFee(sellingPrice),
                        getCurrencyNameById(selectedCurrencyId)
                      )}
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between mb-5">
                  <p>
                    {t('modal.serviceFee', {
                      percentage: showPercentage(
                        getServiceRate(selectedCurrencyId)
                      ),
                    })}
                  </p>
                  <div className="text-right">
                    <div className="price color-popup">
                      {displayCurrency(
                        getServiceFee(sellingPrice),
                        getCurrencyNameById(selectedCurrencyId)
                      )}
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between mb-5">
                  <p>
                    {t('modal.handlingFee', {
                      percentage: showPercentage(
                        getHandlingRate(selectedCurrencyId)
                      ),
                    })}
                  </p>
                  <div className="text-right">
                    <div className="price color-popup">
                      {displayCurrency(
                        getHandlingFee(sellingPrice),
                        getCurrencyNameById(selectedCurrencyId)
                      )}
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between mb-5">
                  <p>{t('modal.youWillReceive')}</p>
                  <div className="text-right">
                    <div className="price color-popup">
                      {displayCurrency(
                        getTotalPrice(sellingPrice),
                        getCurrencyNameById(selectedCurrencyId)
                      )}
                    </div>
                  </div>
                </div>
                <button
                  aria-label={t('modal.sell')}
                  className="btn btn-primary mt-4"
                  data-test="sell-btn"
                  type="submit"
                >
                  <span>{t('modal.sell')}</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateSaleModal;
